import React, { useState } from 'react';
import { 
  User, Mail, Phone, Building2, MapPin, Globe, Shield, Key, Lock, 
  Smartphone, Bell, CheckCircle2, AlertCircle, Save, Camera, Sparkles, 
  Award, TrendingUp, Target, BarChart2, Calendar, Clock, LogOut, Check,
  ChevronRight, RefreshCw, Eye, EyeOff, ShieldCheck, Download, Share2,
  Sliders, Laptop, Cpu, Activity, Briefcase, Medal, Crown, Zap
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { View } from '../types';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

interface UserProfileViewProps {
  setCurrentView?: (view: View) => void;
}

const AVATAR_PRESETS = [
  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300&auto=format&fit=crop&q=80',
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&auto=format&fit=crop&q=80',
  'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=300&auto=format&fit=crop&q=80',
  'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=300&auto=format&fit=crop&q=80',
  'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=300&auto=format&fit=crop&q=80',
  'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=300&auto=format&fit=crop&q=80',
];

const EXECUTIVE_BADGES = [
  { id: 'b1', title: 'Fundador Cyzor', category: 'Fundador', level: 'Master', icon: Crown, color: 'text-amber-700 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400 border-amber-200/90 dark:border-amber-800/60', desc: 'Liderança desde a fundação inicial da plataforma' },
  { id: 'b2', title: 'Pioneiro Copilot IA', category: 'Inovação', level: 'Elite', icon: Sparkles, color: 'text-indigo-700 bg-indigo-50 dark:bg-indigo-950/40 dark:text-indigo-400 border-indigo-200/90 dark:border-indigo-800/60', desc: 'Adoção estratégica de agentes autônomos' },
  { id: 'b3', title: 'Mestre Governança', category: 'Compliance', level: 'Nível III', icon: ShieldCheck, color: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400 border-emerald-200/90 dark:border-emerald-800/60', desc: '100% de conformidade corporativa e auditoria' },
  { id: 'b4', title: 'Zero Delay Culture', category: 'Execução', level: 'Gold', icon: Target, color: 'text-blue-700 bg-blue-50 dark:bg-blue-950/40 dark:text-blue-400 border-blue-200/90 dark:border-blue-800/60', desc: '100% das entregas dentro do cronograma' },
  { id: 'b5', title: 'Impacto ROI 10x', category: 'Finanças', level: 'Top 1%', icon: TrendingUp, color: 'text-purple-700 bg-purple-50 dark:bg-purple-950/40 dark:text-purple-400 border-purple-200/90 dark:border-purple-800/60', desc: 'Retorno sobre investimento comprovado' },
];

const EXECUTIVE_ATTRIBUTES = [
  { attribute: 'Liderança', score: 96 },
  { attribute: 'Estratégia', score: 98 },
  { attribute: 'Execução', score: 92 },
  { attribute: 'Inovação', score: 94 },
  { attribute: 'Finanças', score: 90 },
  { attribute: 'Governança', score: 95 },
];

const CAREER_MILESTONES = [
  { year: '2026', title: 'Direção Executiva de Expansão Global', desc: 'Aprovação do plano estratégico de entrada no mercado LATAM & US.' },
  { year: '2025', title: 'Implementação de IA Copilot Corporativo', desc: 'Redução de 34% no tempo de processos de decisão e governança.' },
  { year: '2024', title: 'Fundação & Lançamento da Plataforma Cyzor', desc: 'Estruturação do centro de operações e primeira rodada de captação.' },
];

const ACTIVE_SESSIONS = [
  { id: 's1', device: 'MacBook Pro 16" (M3 Max)', browser: 'Google Chrome 124.0', location: 'São Paulo, Brasil', ip: '189.122.45.10', isCurrent: true, lastActive: 'Agora' },
  { id: 's2', device: 'iPhone 15 Pro Max', browser: 'Cyzor Mobile App', location: 'São Paulo, Brasil', ip: '189.122.45.12', isCurrent: false, lastActive: 'Há 18 min' },
  { id: 's3', device: 'iPad Air (M2)', browser: 'Safari Mobile', location: 'Rio de Janeiro, Brasil', ip: '177.92.12.88', isCurrent: false, lastActive: 'Ontem às 19:40' },
];

export default function UserProfileView({ setCurrentView }: UserProfileViewProps) {
  const { user, dbUser, updateUserProfile, activeWorkspace, logout } = useAuth();
  const { showToast } = useUI();

  const [activeTab, setActiveTab] = useState<'dados' | 'desempenho' | 'seguranca' | 'preferencias' | 'conquistas'>('dados');
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [customAvatarUrl, setCustomAvatarUrl] = useState('');
  const [status, setStatus] = useState<'online' | 'away' | 'busy' | 'focus'>('online');
  const [hoveredBadge, setHoveredBadge] = useState<string | null>(null);

  // Editable Profile Form State
  const [formData, setFormData] = useState({
    displayName: user?.displayName || user?.name || dbUser?.displayName || 'Carlos Executive',
    title: dbUser?.role || activeWorkspace?.role || 'Diretor Executivo / CEO',
    email: user?.email || 'admin@cyzor.com.br',
    phone: dbUser?.phone || '+55 (11) 99887-6655',
    department: dbUser?.department || 'Conselho de Administração',
    location: dbUser?.location || 'São Paulo, SP - Brasil',
    bio: dbUser?.bio || 'Líder focado em alta performance, inovação tecnológica e escala sustentável para governança de negócios de alta complexidade.',
    photoURL: user?.photoURL || dbUser?.avatarUrl || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300&auto=format&fit=crop&q=80',
    timezone: 'America/Sao_Paulo (UTC-3)',
    language: 'Português (Brasil)',
  });

  // Password Change State
  const [passwordState, setPasswordState] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    showPassword: false,
  });

  // Notification Preferences State
  const [notifications, setNotifications] = useState({
    dailyBriefing: true,
    realtimeAlerts: true,
    weeklyDigest: true,
    financialWarnings: true,
    securityAlerts: true,
    smsAlerts: false,
  });

  // Security Toggles
  const [mfaEnabled, setMfaEnabled] = useState(true);

  const handleSaveProfile = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (updateUserProfile) {
      updateUserProfile({
        displayName: formData.displayName,
        fullName: formData.displayName,
        photoURL: formData.photoURL,
        avatarUrl: formData.photoURL,
        role: formData.title,
        department: formData.department,
        phone: formData.phone,
        location: formData.location,
        bio: formData.bio,
      });
    } else {
      showToast('Perfil atualizado com sucesso!');
    }
  };

  const handleAvatarSelect = (url: string) => {
    setFormData(prev => ({ ...prev, photoURL: url }));
    setIsAvatarModalOpen(false);
    if (updateUserProfile) {
      updateUserProfile({ photoURL: url, avatarUrl: url });
    }
  };

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordState.currentPassword) {
      showToast('Informe a senha atual.');
      return;
    }
    if (passwordState.newPassword.length < 8) {
      showToast('A nova senha deve possuir pelo menos 8 caracteres.');
      return;
    }
    if (passwordState.newPassword !== passwordState.confirmPassword) {
      showToast('A confirmação de senha não confere com a nova senha.');
      return;
    }
    showToast('Senha alterada com sucesso e credenciais atualizadas.');
    setPasswordState({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      showPassword: false,
    });
  };

  const statusConfig = {
    online: { label: 'Online', color: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    away: { label: 'Ausente', color: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
    busy: { label: 'Em Reunião', color: 'bg-rose-500', badge: 'bg-rose-50 text-rose-700 border-rose-200' },
    focus: { label: 'Modo Foco Estratégico', color: 'bg-indigo-500', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  };

  return (
    <div className="flex flex-col gap-8 w-full px-4 sm:px-6 lg:px-10 pb-16 animate-in fade-in duration-300">
      
      {/* Top Banner & Profile Header - Executive Minimal Design */}
      <div className="relative rounded-2xl bg-white dark:bg-[#0F172A] text-slate-900 dark:text-white p-6 sm:p-7 overflow-hidden shadow-2xs border border-slate-200 dark:border-slate-800 transition-colors">
        
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            {/* Executive Avatar Container */}
            <div className="relative group">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm bg-slate-100 dark:bg-slate-900 shrink-0 relative">
                <img 
                  src={formData.photoURL} 
                  alt={formData.displayName} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                  referrerPolicy="no-referrer"
                />
                <button
                  type="button"
                  onClick={() => setIsAvatarModalOpen(true)}
                  className="absolute inset-0 bg-slate-900/60 backdrop-blur-2xs opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center gap-1 text-white text-xs font-bold transition-opacity cursor-pointer"
                >
                  <Camera size={16} />
                  <span className="text-[10px]">Alterar</span>
                </button>
              </div>

              {/* Status Badge Indicator */}
              <span 
                className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-[#0F172A] ${statusConfig[status].color} shadow-xs`}
                title={`Status: ${statusConfig[status].label}`}
              />
            </div>

            {/* Profile Info */}
            <div className="flex flex-col gap-1.5 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-display font-bold tracking-tight text-slate-900 dark:text-white">
                  {formData.displayName}
                </h1>
                <span className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 text-[10px] font-mono font-bold px-2 py-0.5 rounded-md uppercase tracking-wider">
                  Nível 24 Executivo
                </span>
                <span className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 text-[10px] font-bold px-2 py-0.5 rounded-md flex items-center gap-1">
                  <ShieldCheck size={12} className="text-slate-500 dark:text-slate-400" /> MFA Ativo
                </span>
              </div>

              <p className="text-xs text-slate-600 dark:text-slate-400 font-medium flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-900 dark:text-slate-200">{formData.title}</span>
                <span className="text-slate-300 dark:text-slate-700">•</span>
                <span>{formData.department}</span>
                <span className="text-slate-300 dark:text-slate-700">•</span>
                <span className="flex items-center gap-1"><MapPin size={12} /> {formData.location}</span>
              </p>

              {/* XP Level Progress Bar */}
              <div className="mt-1 flex items-center gap-2.5 max-w-xs">
                <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden border border-slate-200/80 dark:border-slate-700/60">
                  <div className="bg-slate-900 dark:bg-slate-200 h-full rounded-full w-[84.5%]" />
                </div>
                <span className="text-[10px] font-mono font-bold text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  8.450 / 10.000 XP
                </span>
              </div>
            </div>
          </div>

          {/* Action Header Buttons */}
          <div className="flex items-center gap-2 w-full sm:w-auto shrink-0 flex-wrap">
            <button
              type="button"
              onClick={() => handleSaveProfile()}
              className="flex-1 sm:flex-initial px-3.5 py-2 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold text-xs hover:bg-slate-800 dark:hover:bg-slate-100 transition-all shadow-2xs flex items-center justify-center gap-2 cursor-pointer active:scale-95"
            >
              <Save size={14} />
              <span>Salvar Alterações</span>
            </button>
            <button
              type="button"
              onClick={() => showToast('Cartão Executivo copiado.')}
              className="px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700 text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer"
              title="Compartilhar Cartão Executivo"
            >
              <Share2 size={14} />
            </button>
          </div>

        </div>

        {/* Icon-Only Executive Achievement Badges Row */}
        <div className="mt-5 pt-4 border-t border-slate-200/80 dark:border-slate-800 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Medal size={14} className="text-slate-500 dark:text-slate-400" />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">
              Selos Executivos
            </span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {EXECUTIVE_BADGES.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.id}
                  onMouseEnter={() => setHoveredBadge(b.id)}
                  onMouseLeave={() => setHoveredBadge(null)}
                  className="relative group cursor-pointer"
                >
                  <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200/90 dark:border-slate-700/70 text-slate-700 dark:text-slate-300 hover:bg-slate-900 hover:text-white dark:hover:bg-white dark:hover:text-slate-900 hover:border-slate-900 dark:hover:border-white transition-all flex items-center justify-center shadow-2xs">
                    <Icon size={16} />
                  </div>

                  {/* Sleek Tooltip */}
                  {hoveredBadge === b.id && (
                    <div className="absolute left-1/2 -top-11 -translate-x-1/2 z-30 bg-slate-900 dark:bg-slate-800 text-white text-[10px] px-2.5 py-1 rounded-md whitespace-nowrap shadow-xl border border-slate-700/80 pointer-events-none flex flex-col items-center gap-0.5 animate-in fade-in duration-150">
                      <span className="font-bold">{b.title}</span>
                      <span className="text-[9px] text-slate-400">{b.category} • {b.level}</span>
                    </div>
                  )}
                </div>
              );
            })}

            <button
              type="button"
              onClick={() => setActiveTab('conquistas')}
              className="ml-1 text-[11px] font-bold text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white flex items-center gap-1 cursor-pointer transition-colors"
            >
              Ver Galeria <ChevronRight size={12} />
            </button>
          </div>
        </div>

        {/* Executive KPI Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-slate-200/80 dark:border-slate-800">
          <div className="flex flex-col gap-0.5 bg-slate-50/80 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 p-3 rounded-xl">
            <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Projetos Liderados</span>
            <span className="text-lg font-display font-bold text-slate-900 dark:text-white">12 Diretos</span>
          </div>
          <div className="flex flex-col gap-0.5 bg-slate-50/80 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 p-3 rounded-xl">
            <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Score Estratégico</span>
            <span className="text-lg font-display font-bold text-slate-900 dark:text-white">98.4 / 100</span>
          </div>
          <div className="flex flex-col gap-0.5 bg-slate-50/80 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 p-3 rounded-xl">
            <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Time Sob Gestão</span>
            <span className="text-lg font-display font-bold text-slate-900 dark:text-white">28 Membros</span>
          </div>
          <div className="flex flex-col gap-0.5 bg-slate-50/80 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 p-3 rounded-xl">
            <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Impacto Gerado</span>
            <span className="text-lg font-display font-bold text-slate-900 dark:text-white">R$ 2.4M ROI</span>
          </div>
        </div>

      </div>

      {/* Main Content Tabs */}
      <div className="flex flex-col gap-6">
        
        {/* Tab Header Bar */}
        <div className="flex items-center gap-2 border-b border-slate-200/80 pb-3 overflow-x-auto scrollbar-none">
          {[
            { id: 'dados', label: 'Dados Pessoais', icon: User },
            { id: 'desempenho', label: 'Desempenho Estratégico', icon: BarChart2 },
            { id: 'seguranca', label: 'Segurança & Credenciais', icon: Shield },
            { id: 'preferencias', label: 'Preferências', icon: Sliders },
            { id: 'conquistas', label: 'Selos Executivos', icon: Award },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                  isActive
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-white hover:bg-slate-100 text-slate-600 border border-slate-200/70'
                }`}
              >
                <Icon size={15} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab 1: Personal & Executive Info Form */}
        {activeTab === 'dados' && (
          <motion.form 
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }} 
            onSubmit={handleSaveProfile}
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          >
            {/* Main Form (2 cols) */}
            <div className="lg:col-span-2 bg-white border border-slate-200/80 rounded-2xl p-6 shadow-2xs flex flex-col gap-6">
              
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div>
                  <h3 className="text-base font-display font-bold text-slate-900">Informações Pessoais & Corporativas</h3>
                  <p className="text-xs text-slate-500">Mantenha seus dados atualizados para relatórios de governança.</p>
                </div>
                <span className="text-xs font-mono font-bold text-slate-400">ID: USR-9842</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <User size={13} className="text-slate-400" /> Nome Completo
                  </label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={e => setFormData({ ...formData, displayName: e.target.value })}
                    className="px-3.5 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 text-xs font-medium text-slate-800 outline-none transition-all"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <Briefcase size={13} className="text-slate-400" /> Cargo Executivo
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    className="px-3.5 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 text-xs font-medium text-slate-800 outline-none transition-all"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <Mail size={13} className="text-slate-400" /> E-mail Corporativo
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    className="px-3.5 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 text-xs font-medium text-slate-800 outline-none transition-all bg-slate-50/50"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <Phone size={13} className="text-slate-400" /> Telefone / WhatsApp
                  </label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    className="px-3.5 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 text-xs font-medium text-slate-800 outline-none transition-all"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <Building2 size={13} className="text-slate-400" /> Departamento
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={e => setFormData({ ...formData, department: e.target.value })}
                    className="px-3.5 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 text-xs font-medium text-slate-800 outline-none transition-all"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <MapPin size={13} className="text-slate-400" /> Localização / Escritório
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={e => setFormData({ ...formData, location: e.target.value })}
                    className="px-3.5 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 text-xs font-medium text-slate-800 outline-none transition-all"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  Manifesto / Bio Executiva
                </label>
                <textarea
                  rows={3}
                  value={formData.bio}
                  onChange={e => setFormData({ ...formData, bio: e.target.value })}
                  className="p-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 text-xs font-medium text-slate-800 outline-none transition-all resize-none"
                />
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl bg-slate-900 text-white font-bold text-xs hover:bg-slate-800 transition-all flex items-center gap-2 cursor-pointer shadow-2xs"
                >
                  <Save size={15} />
                  <span>Salvar Dados Pessoais</span>
                </button>
              </div>

            </div>

            {/* Sidebar Photo & Quick Status (1 col) */}
            <div className="flex flex-col gap-6">
              
              <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-2xs flex flex-col gap-4">
                <h3 className="text-sm font-display font-bold text-slate-900 pb-3 border-b border-slate-100">
                  Foto de Perfil & Status
                </h3>

                <div className="flex flex-col items-center justify-center gap-3 py-2">
                  <div className="w-24 h-24 rounded-2xl overflow-hidden border border-slate-200 shadow-md bg-slate-900 relative group">
                    <img src={formData.photoURL} alt="Avatar" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setIsAvatarModalOpen(true)}
                      className="absolute inset-0 bg-slate-900/60 backdrop-blur-2xs opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs font-bold cursor-pointer transition-opacity"
                    >
                      Alterar
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsAvatarModalOpen(true)}
                    className="text-xs font-bold text-indigo-600 hover:text-indigo-700 cursor-pointer"
                  >
                    Escolher Avatar / Preset
                  </button>
                </div>

                <div className="flex flex-col gap-2 pt-3 border-t border-slate-100">
                  <span className="text-xs font-bold text-slate-700">Definir Status de Disponibilidade:</span>
                  <div className="grid grid-cols-2 gap-2">
                    {(['online', 'busy', 'away', 'focus'] as const).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setStatus(s)}
                        className={`p-2 rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer border ${
                          status === s
                            ? `${statusConfig[s].badge} shadow-2xs`
                            : 'bg-white hover:bg-slate-50 text-slate-600 border-slate-200'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${statusConfig[s].color}`} />
                        <span className="truncate">{statusConfig[s].label}</span>
                      </button>
                    ))}
                  </div>
                </div>

              </div>

              {/* Workspace Belonging */}
              <div className="bg-slate-900 text-white rounded-2xl p-5 shadow-sm border border-slate-800 flex flex-col gap-3">
                <div className="flex items-center gap-2 text-xs font-bold text-indigo-300 uppercase tracking-wider">
                  <Building2 size={14} /> Workspace Principal
                </div>
                <div className="flex flex-col">
                  <span className="text-base font-display font-bold text-white">{activeWorkspace?.name || 'Cyzor Corporate HQ'}</span>
                  <span className="text-xs text-slate-400">Plano Enterprise • Acesso Ilimitado</span>
                </div>
              </div>

            </div>
          </motion.form>
        )}

        {/* Tab 2: Performance & Strategic Radar */}
        {activeTab === 'desempenho' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Competencies Radar Chart */}
            <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-2xs flex flex-col gap-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div>
                  <h3 className="text-base font-display font-bold text-slate-900">Atributos Executivos & Competências</h3>
                  <p className="text-xs text-slate-500">Métricas consolidadas de liderança e impacto organizacional.</p>
                </div>
                <span className="text-xs font-mono font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">
                  Score 98.4
                </span>
              </div>

              <div className="h-72 w-full pt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart cx="50%" cy="50%" outerRadius="80%" data={EXECUTIVE_ATTRIBUTES}>
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis dataKey="attribute" tick={{ fill: '#475569', fontSize: 11, fontWeight: 700 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 9 }} />
                    <Radar name="Performance" dataKey="score" stroke="#4f46e5" fill="#6366f1" fillOpacity={0.35} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Career Timeline */}
            <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-2xs flex flex-col gap-4">
              <div className="pb-3 border-b border-slate-100">
                <h3 className="text-base font-display font-bold text-slate-900">Marcos Históricos & Trajetória</h3>
                <p className="text-xs text-slate-500">Linha do tempo de contribuição na organização.</p>
              </div>

              <div className="flex flex-col gap-6 pt-2">
                {CAREER_MILESTONES.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 relative">
                    <div className="w-10 h-10 rounded-xl bg-slate-900 text-white font-mono font-bold text-xs flex items-center justify-center shrink-0 shadow-2xs">
                      {item.year}
                    </div>
                    <div className="flex flex-col gap-1 min-w-0">
                      <span className="text-xs font-bold text-slate-900">{item.title}</span>
                      <span className="text-xs text-slate-500 leading-relaxed">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </motion.div>
        )}

        {/* Tab 3: Security & Credentials */}
        {activeTab === 'seguranca' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col gap-6">
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Password Change Form */}
              <form onSubmit={handlePasswordChange} className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-2xs flex flex-col gap-4">
                <div className="pb-3 border-b border-slate-100">
                  <h3 className="text-base font-display font-bold text-slate-900 flex items-center gap-2">
                    <Key size={16} className="text-indigo-600" /> Alterar Senha de Acesso
                  </h3>
                  <p className="text-xs text-slate-500">Mantenha credenciais fortes para proteção de dados sigilosos.</p>
                </div>

                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-700">Senha Atual</label>
                    <input
                      type="password"
                      value={passwordState.currentPassword}
                      onChange={e => setPasswordState({ ...passwordState, currentPassword: e.target.value })}
                      className="px-3.5 py-2 rounded-xl border border-slate-200 focus:border-indigo-500 text-xs outline-none"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-700">Nova Senha</label>
                    <input
                      type="password"
                      value={passwordState.newPassword}
                      onChange={e => setPasswordState({ ...passwordState, newPassword: e.target.value })}
                      className="px-3.5 py-2 rounded-xl border border-slate-200 focus:border-indigo-500 text-xs outline-none"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-700">Confirmar Nova Senha</label>
                    <input
                      type="password"
                      value={passwordState.confirmPassword}
                      onChange={e => setPasswordState({ ...passwordState, confirmPassword: e.target.value })}
                      className="px-3.5 py-2 rounded-xl border border-slate-200 focus:border-indigo-500 text-xs outline-none"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="mt-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white font-bold text-xs hover:bg-indigo-700 transition-all cursor-pointer shadow-2xs"
                >
                  Atualizar Credenciais
                </button>
              </form>

              {/* Two-Factor Authentication Box */}
              <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-2xs flex flex-col justify-between gap-4">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                    <h3 className="text-base font-display font-bold text-slate-900 flex items-center gap-2">
                      <ShieldCheck size={18} className="text-emerald-600" /> Autenticação em Duas Etapas (2FA)
                    </h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${mfaEnabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {mfaEnabled ? 'Ativado' : 'Desativado'}
                    </span>
                  </div>

                  <p className="text-xs text-slate-600 leading-relaxed">
                    A verificação em dois fatores exige um código de segurança temporário gerado no seu aplicativo autenticador a cada novo login.
                  </p>

                  <div className="bg-slate-50 border border-slate-200 p-3.5 rounded-xl flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <Smartphone size={20} className="text-slate-600" />
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-slate-900">Authenticator App (TOTP)</span>
                        <span className="text-[10px] text-slate-500">Google Authenticator, 1Password ou Authy</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setMfaEnabled(!mfaEnabled);
                        showToast(mfaEnabled ? '2FA temporariamente pausado.' : '2FA ativado com sucesso.');
                      }}
                      className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition-all cursor-pointer"
                    >
                      {mfaEnabled ? 'Gerenciar 2FA' : 'Ativar 2FA'}
                    </button>
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                  <span>Último teste de segurança: Hoje às 09:12</span>
                  <span className="font-bold text-emerald-600">100% Protegido</span>
                </div>
              </div>

            </div>

            {/* Active Login Sessions Table */}
            <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-2xs flex flex-col gap-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div>
                  <h3 className="text-base font-display font-bold text-slate-900">Sessões Ativas de Login</h3>
                  <p className="text-xs text-slate-500">Dispositivos conectados à sua conta executiva no Cyzor Control.</p>
                </div>
                <button
                  type="button"
                  onClick={() => showToast('Todas as outras sessões foram encerradas.')}
                  className="px-3 py-1.5 rounded-xl bg-rose-50 text-rose-700 border border-rose-200 text-xs font-bold hover:bg-rose-100 transition-all cursor-pointer"
                >
                  Encerrar Outras Sessões
                </button>
              </div>

              <div className="divide-y divide-slate-100">
                {ACTIVE_SESSIONS.map((s) => (
                  <div key={s.id} className="py-3.5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                        <Laptop size={18} />
                      </div>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-900">{s.device}</span>
                          {s.isCurrent && (
                            <span className="text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.2 rounded">
                              Este Dispositivo
                            </span>
                          )}
                        </div>
                        <span className="text-[11px] text-slate-500">{s.browser} • {s.location} ({s.ip})</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400 font-medium">{s.lastActive}</span>
                      {!s.isCurrent && (
                        <button
                          type="button"
                          onClick={() => showToast(`Sessão ${s.device} desativada.`)}
                          className="text-xs font-bold text-rose-600 hover:text-rose-700 cursor-pointer"
                        >
                          Revogar
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </motion.div>
        )}

        {/* Tab 4: Preferences */}
        {activeTab === 'preferencias' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-2xs flex flex-col gap-6">
            <div className="pb-3 border-b border-slate-100">
              <h3 className="text-base font-display font-bold text-slate-900">Preferências do Sistema & Notificações</h3>
              <p className="text-xs text-slate-500">Ajuste como o Cyzor envia alertas e sintetiza briefings corporativos.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { key: 'dailyBriefing', label: 'Briefing Executivo Diário (08:00)', desc: 'Resumo automatizado por IA das métricas, vendas e alertas críticos.' },
                { key: 'realtimeAlerts', label: 'Alertas de Mudança em Projetos', desc: 'Notificações imediatas sobre alteração de status ou riscos.' },
                { key: 'weeklyDigest', label: 'Relatório Consolidado Semanal', desc: 'Enviado toda segunda-feira com curva de ROI e performance de squad.' },
                { key: 'financialWarnings', label: 'Avisos de Vencimento Fiscais / Financeiros', desc: 'Alertas prévios sobre contas, faturas ou desvios orçamentários.' },
              ].map((item) => (
                <div key={item.key} className="p-4 rounded-xl border border-slate-200/80 bg-slate-50/50 flex items-start justify-between gap-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-bold text-slate-900">{item.label}</span>
                    <span className="text-[11px] text-slate-500 leading-relaxed">{item.desc}</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={(notifications as any)[item.key]}
                    onChange={e => {
                      setNotifications({ ...notifications, [item.key]: e.target.checked });
                      showToast('Preferências atualizadas.');
                    }}
                    className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 mt-0.5 cursor-pointer"
                  />
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Tab 5: Executive Badges */}
        {activeTab === 'conquistas' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: 'Fundador Cyzor', desc: 'Liderança desde o início das operações.', icon: '🏆', date: 'Jan 2024' },
              { title: 'Visão 10x Estratégica', desc: 'Conclusão de 10 rodadas semestrais de OKRs.', icon: '🎯', date: 'Dez 2024' },
              { title: 'Pioneiro em IA', desc: 'Pioneirismo na adoção de agentes autônomos na empresa.', icon: '⚡', date: 'Mar 2025' },
              { title: 'Excelência Financeira', desc: 'Manutenção de margem de lucro EBITDA acima de 35%.', icon: '📈', date: 'Ago 2025' },
              { title: 'Zero Delay Culture', desc: '100% de entregas estratégicas rigorosamente no prazo.', icon: '🛡️', date: 'Fev 2026' },
              { title: 'Mestre da Governança', desc: 'Auditoria corporativa completa sem pendências.', icon: '💎', date: 'Jul 2026' },
            ].map((b, i) => (
              <div key={i} className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-2xs flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-2xl">{b.icon}</span>
                  <span className="text-[10px] font-mono font-bold text-slate-400">{b.date}</span>
                </div>
                <span className="text-xs font-bold text-slate-900 mt-1">{b.title}</span>
                <span className="text-[11px] text-slate-500 leading-snug">{b.desc}</span>
              </div>
            ))}
          </motion.div>
        )}

      </div>

      {/* Modal for Avatar Selection */}
      {isAvatarModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl border border-slate-200 p-6 max-w-md w-full shadow-2xl flex flex-col gap-4">
            <h3 className="text-base font-display font-bold text-slate-900">Selecionar Avatar Executivo</h3>
            
            <div className="grid grid-cols-3 gap-3 my-2">
              {AVATAR_PRESETS.map((url, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleAvatarSelect(url)}
                  className="w-full aspect-square rounded-xl overflow-hidden border-2 border-slate-200 hover:border-indigo-600 transition-all cursor-pointer group"
                >
                  <img src={url} alt={`Avatar ${i}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-1.5 pt-2 border-t border-slate-100">
              <label className="text-xs font-bold text-slate-700">Ou informe a URL da imagem:</label>
              <div className="flex items-center gap-2">
                <input
                  type="url"
                  placeholder="https://..."
                  value={customAvatarUrl}
                  onChange={e => setCustomAvatarUrl(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-xs outline-none focus:border-indigo-500"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (customAvatarUrl) handleAvatarSelect(customAvatarUrl);
                  }}
                  className="px-3 py-2 bg-slate-900 text-white font-bold text-xs rounded-xl hover:bg-slate-800 cursor-pointer"
                >
                  Aplicar
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsAvatarModalOpen(false)}
              className="mt-2 text-xs font-bold text-slate-500 hover:text-slate-800 cursor-pointer text-center"
            >
              Cancelar
            </button>
          </motion.div>
        </div>
      )}

    </div>
  );
}
